'use client';

import {
  Box,
  Button,
  Divider,
  IconButton,
  SystemIcon,
  Typography,
  CheckboxListItem,
} from '@wishket/design-system';
import {
  useRef,
  useState,
  FormEvent,
  useEffect,
  PropsWithChildren,
} from 'react';
import { twMerge } from 'tailwind-merge';

import { Icon } from '@/shared/Components';
import { getScrollbarStyle } from '@/shared/utils';

interface ContainerProps extends PropsWithChildren {
  isDesktop: boolean;
  onClose: () => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
}

interface ContentsProps extends PropsWithChildren {
  onClose: () => void;
}

interface CheckLabelProps {
  open: boolean;
  label: string;
  testId?: string;
  checked: boolean;
  onOpen: () => void;
  onChecked: () => void;
}

interface GradientScrollContainerProps extends PropsWithChildren {
  className?: string;
}

export const Container = ({
  children,
  isDesktop,
  onClose,
  onSubmit,
}: ContainerProps) => {
  const Contents = isDesktop ? DesktopContents : MobileContents;

  return (
    <form
      data-testid="alarm-setting-modal"
      className="flex h-full w-full flex-col desktop:max-h-[80vh]"
      onSubmit={onSubmit}
    >
      <Contents onClose={onClose}>{children}</Contents>
      <Divider />
      <Box className="flex px-5 py-4 tablet:px-8 tablet:py-5">
        <Button type="submit" variant="solid" className="w-full">
          전체 동의하고 뉴스레터 구독하기
        </Button>
      </Box>
    </form>
  );
};

const ContentsTitle = () => {
  return (
    <Box className="flex flex-col gap-4 desktop:items-center">
      <Box className="size-[60px]">
        <Icon name="flying_rocket" />
      </Box>
      <Box className="flex flex-col desktop:items-center">
        <Typography variant="title3" className="font-medium text-w-gray-900">
          성장을 위한 실무 인사이트
        </Typography>
        <Typography variant="title3" className="font-medium text-w-gray-900">
          매주 목요일 오후에 만나보세요!
        </Typography>
      </Box>
    </Box>
  );
};

const ContentsInfo = () => {
  return (
    <Box className="flex flex-col gap-6">
      <Box className="flex items-center gap-6">
        <Box className="flex size-12 items-center justify-center rounded-full bg-w-gray-10 p-3">
          <Icon name="note" />
        </Box>
        <Box className="flex flex-col gap-1">
          <Typography variant="body1" className="font-medium text-w-gray-900">
            실무에 유용한 정보만
          </Typography>
          <Typography variant="body1" className="text-w-gray-600">
            에디터가 직접 IT 실무 콘텐츠를 엄선해 드려요.
          </Typography>
        </Box>
      </Box>
      <Box className="flex items-center gap-6">
        <Box className="flex size-12 items-center justify-center rounded-full bg-w-gray-10 p-3">
          <Icon name="fire" />
        </Box>
        <Box className="flex flex-col gap-1">
          <Typography variant="body1" className="font-medium text-w-gray-900">
            인기 콘텐츠도 한 번에
          </Typography>
          <Typography variant="body1" className="text-w-gray-600">
            매주 독자에게 인기있는 콘텐츠도 확인할 수 있어요.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const DesktopContents = ({ children, onClose }: ContentsProps) => {
  return (
    <Box className="relative overflow-y-auto">
      <Box className="absolute left-0 right-0 top-0 h-[200px] bg-gradient-marketing" />
      <IconButton
        type="button"
        className="fixed right-8 top-8 z-20"
        data-testid="alarm-setting-modal--close-button"
        onClick={onClose}
      >
        <SystemIcon name="large_delete" className="text-w-gray-600" />
      </IconButton>
      <Box
        className={twMerge(
          'flex flex-1 flex-col overflow-y-scroll pb-8 pt-12',
          getScrollbarStyle()
        )}
      >
        <Box className="z-10 mb-8">
          <ContentsTitle />
        </Box>
        <Box className="mb-12">
          <ContentsInfo />
        </Box>
        {children}
      </Box>
    </Box>
  );
};

const MobileContents = ({ children, onClose }: ContentsProps) => {
  return (
    <>
      <Box>
        <Box className="relative flex items-center justify-center px-5 py-4 desktop:justify-start desktop:px-8 desktop:py-6">
          <Typography
            variant="subTitle1"
            className="font-medium text-w-gray-900 desktop:typo-subTitle2"
          >
            알림 설정
          </Typography>
          <IconButton
            type="button"
            data-testid="alarm-setting-modal--close-button"
            className="absolute right-5 top-1/2 -translate-y-1/2 transform desktop:right-8"
            onClick={onClose}
          >
            <SystemIcon name="large_delete" className="text-w-gray-600" />
          </IconButton>
        </Box>
        <Divider />
      </Box>
      <Box
        className={twMerge(
          'flex flex-1 flex-col gap-8 overflow-y-scroll p-5 pt-6',
          getScrollbarStyle()
        )}
      >
        <ContentsTitle />
        <ContentsInfo />
        {children}
      </Box>
    </>
  );
};

export const CheckboxListItemWithInfo = ({
  open,
  label,
  testId,
  checked,
  onOpen,
  onChecked,
}: CheckLabelProps) => {
  return (
    <Box className="flex items-center justify-between">
      <CheckboxListItem
        type="sub"
        checked={checked}
        data-testid={testId}
        // TODO: 디자인시스템 서브 체크박스 그룹 호버값 제거
        CheckboxClassName={twMerge(
          'cursor-pointer',
          !checked && 'group-hover:text-w-gray-200'
        )}
        onChange={onChecked}
      >
        <Typography variant="body1" className="text-w-gray-900">
          {label} <span className="text-w-gray-400">(선택)</span>
        </Typography>
      </CheckboxListItem>
      <Box
        onClick={onOpen}
        data-testid="arrow-container"
        className={twMerge(
          'transition-transform duration-300',
          open && 'rotate-180'
        )}
      >
        <SystemIcon
          name="medium_arrow_down"
          className="cursor-pointer text-w-gray-400"
        />
      </Box>
    </Box>
  );
};

export const GradientScrollContainer = ({
  children,
  className,
}: GradientScrollContainerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState<boolean>(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      setIsScrolledToEnd(scrollTop + clientHeight >= scrollHeight - 1);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      handleScroll();
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  return (
    <Box className="relative">
      <Box
        ref={containerRef}
        data-testid="gradient-scroll-container"
        className={twMerge(
          `h-[120px] overflow-auto scrollbar-list `,
          className
        )}
      >
        {children}
      </Box>
      {!isScrolledToEnd && (
        <Box className="pointer-events-none absolute bottom-0 h-8 w-full bg-gradient-to-b from-transparent to-w-gray-10" />
      )}
    </Box>
  );
};
